import React, { useState, useEffect, useMemo, ReactElement } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  EditIcon,
  BuildingIcon,
  FileTextIcon,
  DollarSignIcon,
  UserIcon,
  PlusIcon,
  SaveIcon,
  MailIcon,
  MoreHorizontalIcon,
  CalendarIcon,
  BriefcaseIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { MobilePage, MobilePageHeader, MobileSheet, mobile } from '../components/mobile'
import { ViewTabBar, ViewSection, ViewStatCard, ViewEmptyState } from '../components/ViewPageLayout'
import { ConversationsPanel } from '../components/ConversationsPanel'
import { getEntityActivities, ActivityLog, logActivity, ActivityTypes } from '../utils/activityLogger'
import { NotesAndActivityPanel } from '../components/NotesAndActivityPanel'
import { ConvertLeadModal } from '../components/ConvertLeadModal'
import { resolveOwnerContact } from '../lib/resolveOwnerContact'
import { ProposalRequestTabPanel } from '../components/ProposalRequestTabPanel'
import {
  resolveProposalRequestFromProposal,
  type ProposalRequestDisplayData,
} from '../lib/proposalRequestDisplay'
import { getServiceTypeLabel } from '../lib/serviceTypeOptions'
import { proposalToDocumentProps } from '../lib/documentProps'
import { downloadDocumentPdf } from '../lib/downloadDocumentPdf'
import { fetchProposalMilestones } from '../lib/milestoneBilling'
import MilestoneScheduleEditor from '../components/agreement/MilestoneScheduleEditor'
import type { BillingMilestone } from '../types/agreement'

interface ProposalNote {
  timestamp: string
  note: string
}

export default function ProposalView() {
  console.log('🚀 ProposalView component loaded - NEW VERSION WITH UPDATED CONVERSION MODAL')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const [loading, setLoading] = useState(true)

  const [activeTab, setActiveTab] = useState<'overview' | 'billing' | 'items' | 'notes' | 'request'>('overview')
  const [convertedContractInfo, setConvertedContractInfo] = useState<{ id: string; contract_number?: string; title?: string } | null>(null)

  const [proposal, setProposal] = useState<any>(null)
  const [customer, setCustomer] = useState<any>(null)
  const [lead, setLead] = useState<any>(null)
  const [property, setProperty] = useState<any>(null)
  const [project, setProject] = useState<any>(null)
  const [notes, setNotes] = useState<ProposalNote[]>([])
  const [activities, setActivities] = useState<ActivityLog[]>([])
  const [newNote, setNewNote] = useState('')
  const [savingNote, setSavingNote] = useState(false)
  const [lineItems, setLineItems] = useState<any[]>([])
  const [isEditingStatus, setIsEditingStatus] = useState(false)
  const [statusValue, setStatusValue] = useState<string>('')
  const [savingStatus, setSavingStatus] = useState(false)
  const [showConvertModal, setShowConvertModal] = useState(false)
  const [pendingStatus, setPendingStatus] = useState<string | null>(null)
  const [pendingAction, setPendingAction] = useState<'create-contract' | 'create-job' | null>(null)
  const [showCreateJobModal, setShowCreateJobModal] = useState(false)
  const [proposalRequest, setProposalRequest] = useState<ProposalRequestDisplayData | null>(null)
  const [loadingRequest, setLoadingRequest] = useState(false)
  const [serviceCategories, setServiceCategories] = useState<any[]>([])
  const [generatingPdf, setGeneratingPdf] = useState(false)
  const [billingMilestones, setBillingMilestones] = useState<BillingMilestone[]>([])
  const [actionsOpen, setActionsOpen] = useState(false)

  const handleGeneratePdf = async () => {
    if (!proposal) return
    setGeneratingPdf(true)
    try {
      let prop = property
      const propertyId = proposal.property_id
      if (
        !prop?.address &&
        propertyId &&
        typeof propertyId === 'string' &&
        !propertyId.startsWith('project-') &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(propertyId)
      ) {
        const { data: propertyData } = await supabase
          .from('properties')
          .select('*')
          .eq('id', propertyId)
          .single()
        if (propertyData) prop = propertyData
      }

      const milestoneRows =
        proposal.billing_frequency === 'progress' ? billingMilestones : []
      const props = proposalToDocumentProps(proposal, {
        customer,
        lead,
        property: prop,
        project,
        lineItems,
        billingMilestones: milestoneRows.map((m) => ({
          label: m.label,
          percentage: m.percentage,
          amount: m.amount,
        })),
      })
      const fileName = `Proposal-${proposal.proposal_number || proposal.id}`
      await downloadDocumentPdf(props, fileName)
    } catch (err) {
      console.error('Error generating PDF:', err)
      alert('Could not generate the PDF: ' + (err as any)?.message)
    } finally {
      setGeneratingPdf(false)
    }
  }

  const proposalRequestContact = useMemo(() => {
    if (!proposalRequest) return null
    return resolveOwnerContact({
      customer,
      lead,
      property,
      leadProject: project,
    })
  }, [proposalRequest, customer, lead, property, project])

  // Helper function to format phone number as clickable link
  const formatPhoneLink = (phone: string | null | undefined): ReactElement | string => {
    if (!phone || phone === 'N/A') return 'N/A'
    // Clean phone number for tel: link (remove non-digit characters except +)
    const cleanPhone = phone.replace(/[^\d+]/g, '')
    return (
      <a
        href={`tel:${cleanPhone}`}
        className="text-blue-600 hover:text-blue-800 underline"
      >
        {phone}
      </a>
    )
  }

  // Helper function to format website URL as clickable link
  const formatWebsiteLink = (website: string | null | undefined): ReactElement | string => {
    if (!website || website === 'N/A') return 'N/A'
    // Ensure URL has protocol
    const url = website.startsWith('http://') || website.startsWith('https://')
      ? website
      : `https://${website}`
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 underline"
      >
        {website}
      </a>
    )
  }

  useEffect(() => {
    if (id) {
      fetchProposalData()
      fetchActivities()
      fetchProposalLineItems()
    }
    fetchServiceCategories()
    console.log('ProposalView mounted - Request tab should be visible')
  }, [id])

  // Re-fetch data when returning from edit or when window regains focus (user might have edited in another tab)
  useEffect(() => {
    const handleFocus = () => {
      if (id) {
        fetchProposalData()
        fetchProposalLineItems()
      }
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [id])

  useEffect(() => {
    if (proposal) {
      fetchProposalRequest()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal])

  useEffect(() => {
    if (proposal) {
      setStatusValue(proposal.status || 'draft')
    }
  }, [proposal])

  const fetchProposalLineItems = async () => {
    if (!id) return
    try {
      const { data, error } = await supabase
        .from('proposal_line_items')
        .select(
          `
          *,
          service_item:service_items(id, name)
        `
        )
        .eq('proposal_id', id)
        .order('sort_order', { ascending: true })

      if (error) {
        // Table might not exist yet, that's okay
        console.warn('Error fetching proposal line items (table may not exist):', error)
        return
      }

      console.log('Fetched proposal line items:', data)
      setLineItems(data || [])
    } catch (error) {
      console.error('Error fetching proposal line items:', error)
    }
  }

  const fetchServiceCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('service_categories')
        .select('id, name')
        .order('name')

      if (error) throw error
      setServiceCategories(data || [])
    } catch (error) {
      console.error('Error fetching service categories:', error)
    }
  }

  const getServiceCategoryName = (categoryId: string) => {
    const category = serviceCategories.find((c) => c.id === categoryId)
    return category ? category.name : categoryId
  }

  const fetchProposalData = async () => {
    try {
      // Fetch proposal with project_address field
      const { data: proposalData, error: proposalError } = await supabase
        .from('proposals')
        .select('*, project_address')
        .eq('id', id)
        .single()

      if (proposalError) throw proposalError
      setProposal(proposalData)

      if (proposalData.billing_frequency === 'progress') {
        try {
          const milestones = await fetchProposalMilestones(proposalData.id)
          setBillingMilestones(milestones)
        } catch {
          setBillingMilestones([])
        }
      } else {
        setBillingMilestones([])
      }

      // Fetch converted contract info for back-link (Phase 8 alignment)
      if (proposalData?.converted_to_contract_id) {
        try {
          const { data: contractInfo } = await supabase
            .from('contracts')
            .select('id, contract_number, title')
            .eq('id', proposalData.converted_to_contract_id)
            .single()
          if (contractInfo) setConvertedContractInfo(contractInfo)
        } catch (e) {
          console.warn('Could not fetch converted contract info:', e)
        }
      } else {
        setConvertedContractInfo(null)
      }

      // Fetch notes - handle both JSONB array and JSON string
      let parsedNotes: ProposalNote[] = []
      if (proposalData.notes) {
        if (Array.isArray(proposalData.notes)) {
          parsedNotes = proposalData.notes
        } else if (typeof proposalData.notes === 'string') {
          try {
            const parsed = JSON.parse(proposalData.notes)
            if (Array.isArray(parsed)) {
              parsedNotes = parsed
            }
          } catch (e) {
            console.warn('Failed to parse notes as JSON:', e)
          }
        }
      }
      setNotes(parsedNotes)
      console.log('Fetched notes:', parsedNotes)

      // Fetch customer (if exists)
      if (proposalData.customer_id) {
        const { data: customerData, error: customerError } = await supabase
          .from('customers')
          .select('*')
          .eq('id', proposalData.customer_id)
          .single()

        if (!customerError && customerData) {
          setCustomer(customerData)
        }
      }

      // Fetch lead (if exists - may exist even if customer exists for converted leads)
      let leadDataForProject = null
      if (proposalData.lead_id) {
        const { data: leadData, error: leadError } = await supabase
          .from('leads')
          .select('*')
          .eq('id', proposalData.lead_id)
          .single()

        if (!leadError && leadData) {
          setLead(leadData)
          leadDataForProject = leadData
        }
      }

      // Fetch property (for customers)
      if (proposalData.property_id && proposalData.customer_id) {
        // Only fetch if it's a valid UUID (not project-${index})
        if (
          typeof proposalData.property_id === 'string' &&
          !proposalData.property_id.startsWith('project-')
        ) {
          const { data: propertyData, error: propertyError } = await supabase
            .from('properties')
            .select('*')
            .eq('id', proposalData.property_id)
            .single()

          if (!propertyError && propertyData) {
            setProperty(propertyData)
          }
        }
      }

      // Find project (for leads) - match by project_address or property_id
      if (leadDataForProject && leadDataForProject.projects) {
        let matchedProject = null

        // Method 1: Try to match project by project_address stored in proposal
        if (proposalData.project_address) {
          matchedProject = leadDataForProject.projects.find(
            (p: any) => p.address === proposalData.project_address
          )
        }

        // Method 2: Try to match by property_id if it contains project-${index}
        if (
          !matchedProject &&
          proposalData.property_id &&
          typeof proposalData.property_id === 'string' &&
          proposalData.property_id.includes('project-')
        ) {
          const projectIndex = parseInt(proposalData.property_id.replace('project-', ''))
          if (projectIndex >= 0 && leadDataForProject.projects[projectIndex]) {
            matchedProject = leadDataForProject.projects[projectIndex]
          }
        }

        // Method 3: Try to find by project ID (if property_id is not a UUID)
        if (
          !matchedProject &&
          proposalData.property_id &&
          typeof proposalData.property_id === 'string' &&
          !proposalData.property_id.match(
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
          )
        ) {
          matchedProject = leadDataForProject.projects.find(
            (p: any) => p.id === proposalData.property_id
          )
        }

        if (matchedProject) {
          setProject(matchedProject)
        }
      }
    } catch (error) {
      console.error('Error fetching proposal data:', error)
      alert('Error loading proposal: ' + (error as any).message)
    } finally {
      setLoading(false)
    }
  }

  const fetchActivities = async () => {
    try {
      const acts = await getEntityActivities('proposal', id!)
      setActivities(acts)
    } catch (error) {
      console.error('Error fetching activities:', error)
    }
  }

  const fetchProposalRequest = async () => {
    if (!proposal) return

    setLoadingRequest(true)
    try {
      setProposalRequest(resolveProposalRequestFromProposal(proposal))
    } catch (error) {
      console.error('Error resolving proposal request:', error)
      setProposalRequest(null)
    } finally {
      setLoadingRequest(false)
    }
  }

  const handleAddNote = async () => {
    if (!newNote.trim() || !id) return

    setSavingNote(true)
    try {
      const noteToAdd: ProposalNote = {
        timestamp: new Date().toISOString(),
        note: newNote.trim(),
      }

      const updatedNotes = [...notes, noteToAdd]

      console.log('Saving notes:', updatedNotes)

      const { error } = await supabase
        .from('proposals')
        .update({ notes: updatedNotes }) // Supabase should handle JSONB arrays automatically
        .eq('id', id)

      if (error) throw error

      setNotes(updatedNotes)
      setNewNote('')

      // Log activity
      await logActivity({
        activity_type: 'note_added',
        entity_type: 'proposal',
        entity_id: id,
        description: 'Note added to proposal',
      })

      // Refresh proposal data and line items to ensure all fields are up to date
      await fetchProposalData()
      await fetchProposalLineItems()
      fetchActivities()
    } catch (error) {
      console.error('Error adding note:', error)
      alert('Error adding note')
    } finally {
      setSavingNote(false)
    }
  }

  const handleSaveStatus = async () => {
    if (!id || !statusValue || statusValue === proposal.status) {
      setIsEditingStatus(false)
      return
    }

    console.log('Status change check:', {
      newStatus: statusValue,
      oldStatus: proposal.status,
      lead_id: proposal.lead_id,
      customer_id: proposal.customer_id,
      hasLead: !!proposal.lead_id && proposal.lead_id !== '',
      hasCustomer: !!proposal.customer_id,
      leadLoaded: !!lead,
    })

    // If changing to "approved" and proposal is linked to a lead (not a customer), show convert modal
    const isApproving = statusValue === 'approved' && proposal.status !== 'approved'
    const hasLead = proposal.lead_id && proposal.lead_id !== ''
    const hasCustomer = !!proposal.customer_id

    if (isApproving && hasLead && !hasCustomer) {
      // If lead isn't loaded, fetch it
      if (!lead && proposal.lead_id) {
        try {
          const { data: leadData, error: leadError } = await supabase
            .from('leads')
            .select('*')
            .eq('id', proposal.lead_id)
            .single()

          if (!leadError && leadData) {
            setLead(leadData)
            console.log('Lead fetched, showing convert modal')
            setPendingStatus(statusValue)
            setShowConvertModal(true)
            setIsEditingStatus(false)
            return
          } else {
            console.error('Error fetching lead for conversion:', leadError)
          }
        } catch (error) {
          console.error('Error fetching lead:', error)
        }
      } else if (lead) {
        console.log('Showing convert modal')
        setPendingStatus(statusValue)
        setShowConvertModal(true)
        setIsEditingStatus(false)
        return
      }
    }

    console.log('Saving status directly')
    // Otherwise, save status directly
    await updateProposalStatus(statusValue)
  }

  const updateProposalStatus = async (newStatus: string) => {
    if (!id || !newStatus) return

    setSavingStatus(true)
    try {
      const oldStatus = proposal.status
      const { error } = await supabase
        .from('proposals')
        .update({ status: newStatus })
        .eq('id', id)

      if (error) throw error

      // Refresh proposal data to get latest values
      await fetchProposalData()
      setIsEditingStatus(false)

      // Update lead stage to 'proposal_sent' if proposal status is changed to 'sent' and linked to a lead
      if (newStatus === 'sent' && proposal.lead_id) {
        try {
          const { error: leadUpdateError } = await supabase
            .from('leads')
            .update({ stage: 'proposal_sent' })
            .eq('id', proposal.lead_id)

          if (leadUpdateError) {
            console.error('Error updating lead stage:', leadUpdateError)
          } else {
            console.log('✅ Lead stage updated to proposal_sent:', proposal.lead_id)
          }
        } catch (leadError) {
          console.error('Error updating lead stage:', leadError)
          // Don't fail proposal status update if lead update fails
        }
      }

      // Log activity
      await logActivity({
        activity_type: 'status_changed',
        entity_type: 'proposal',
        entity_id: id,
        description: `Proposal status changed from ${oldStatus} to ${newStatus}`,
        metadata: { old_status: oldStatus, new_status: newStatus },
      })

      // Refresh proposal data and line items to ensure all fields are up to date
      await fetchProposalData()
      await fetchProposalLineItems()
      fetchActivities()
    } catch (error) {
      console.error('Error updating status:', error)
      alert('Error updating status')
    } finally {
      setSavingStatus(false)
    }
  }

  const handleProposalConversionSuccess = async (customerId: string) => {
    if (!id) return
    try {
      const { data: customerProperties } = await supabase
        .from('properties')
        .select('id')
        .eq('customer_id', customerId)
        .limit(1)

      const propertyId =
        customerProperties && customerProperties.length > 0 ? customerProperties[0].id : null

      const proposalUpdate: any = {
        customer_id: customerId,
        lead_id: null,
      }
      if (propertyId) proposalUpdate.property_id = propertyId

      const { error: proposalError } = await supabase
        .from('proposals')
        .update(proposalUpdate)
        .eq('id', id)

      if (proposalError) throw proposalError

      await fetchProposalData()
      setShowConvertModal(false)

      if (pendingAction === 'create-contract') {
        setPendingAction(null)
        const returnToUrl = encodeURIComponent(`/proposals/view/${id}`)
        navigate(`/contracts/create?proposalId=${id}&returnTo=${returnToUrl}`)
        return
      }
      if (pendingAction === 'create-job') {
        setPendingAction(null)
        const returnToUrl = encodeURIComponent(`/proposals/view/${id}`)
        navigate(`/jobs/create?proposalId=${id}&returnTo=${returnToUrl}`)
        return
      }

      setPendingStatus(null)
      setShowCreateJobModal(true)
    } catch (error) {
      console.error('Error after lead conversion:', error)
      alert('Error updating proposal: ' + (error as any).message)
    }
  }

  const handleCancelEditStatus = () => {
    setStatusValue(proposal.status || 'draft')
    setIsEditingStatus(false)
  }

  const ensureProposalConverted = async (): Promise<boolean> => {
    // Check if proposal is linked to a lead (not a customer)
    if (proposal.lead_id && !proposal.customer_id) {
      // Need to convert lead to customer
      if (!lead) {
        // Fetch lead if not loaded
        try {
          const { data: leadData, error: leadError } = await supabase
            .from('leads')
            .select('*')
            .eq('id', proposal.lead_id)
            .single()

          if (!leadError && leadData) {
            setLead(leadData)
            setShowConvertModal(true)
            return false
          } else {
            console.error('Error fetching lead for conversion:', leadError)
            alert('Error: Could not load lead information')
            return false
          }
        } catch (error) {
          console.error('Error fetching lead:', error)
          alert('Error: Could not load lead information')
          return false
        }
      } else {
        setShowConvertModal(true)
        return false
      }
    }
    // Already has a customer or no lead, ready to proceed
    return true
  }

  const handleCreateContract = async () => {
    const returnTo = encodeURIComponent(`/proposals/view/${id}`)
    // If already linked to customer/property, just navigate (don't approve yet)
    if (proposal.customer_id && !proposal.lead_id) {
      // Already linked to customer - navigate to contract creation
      // Proposal will be approved when contract is successfully created
      navigate(`/contracts/create?proposalId=${id}&returnTo=${returnTo}`)
      return
    }

    // Otherwise, ensure conversion happens first
    const ready = await ensureProposalConverted()
    if (!ready) {
      // Conversion modal is showing, store action to perform after conversion
      setPendingAction('create-contract')
      return
    }

    // After conversion, navigate to contract creation
    // Proposal will be approved when contract is successfully created
    navigate(`/contracts/create?proposalId=${id}&returnTo=${returnTo}`)
  }

  const handleCreateJob = async () => {
    const returnTo = encodeURIComponent(`/proposals/view/${id}`)
    // If already linked to customer/property, just navigate (don't approve yet)
    if (proposal.customer_id && !proposal.lead_id) {
      // Already linked to customer - navigate to job creation
      // Proposal will be approved when job is successfully created
      navigate(`/jobs/create?proposalId=${id}&returnTo=${returnTo}`)
      return
    }

    // Otherwise, ensure conversion happens first
    const ready = await ensureProposalConverted()
    if (!ready) {
      // Conversion modal is showing, store action to perform after conversion
      setPendingAction('create-job')
      return
    }

    // After conversion, navigate to job creation
    // Proposal will be approved when job is successfully created
    navigate(`/jobs/create?proposalId=${id}&returnTo=${returnTo}`)
  }

  const ensureProposalApproved = async () => {
    if (!id || !proposal) return

    try {
      // Only update if status is not already approved
      if (proposal.status !== 'approved') {
        const { error } = await supabase
          .from('proposals')
          .update({ status: 'approved' })
          .eq('id', id)

        if (error) throw error

        // Log activity
        await logActivity({
          activity_type: ActivityTypes.PROPOSAL_UPDATED,
          entity_type: 'proposal',
          entity_id: id,
          description: 'Proposal approved for contract/job creation',
          metadata: {
            status: 'approved',
          },
        })

        // Refresh proposal data
        await fetchProposalData()
      }
    } catch (error) {
      console.error('Error updating proposal:', error)
      alert('Error updating proposal: ' + (error as any).message)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800'
      case 'sent':
        return 'bg-blue-100 text-blue-800'
      case 'viewed':
        return 'bg-yellow-100 text-yellow-800'
      case 'approved':
        return 'bg-green-100 text-green-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      case 'expired':
        return 'bg-orange-100 text-orange-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return (
      <MobilePage className="flex items-center justify-center min-h-[400px]">
        <p className="text-gray-600">Loading...</p>
      </MobilePage>
    )
  }

  if (!proposal) {
    return (
      <MobilePage className="flex items-center justify-center min-h-[400px]">
        <p className="text-red-600">Proposal not found</p>
      </MobilePage>
    )
  }

  const handleBack = () => {
    if (returnTo) {
      navigate(decodeURIComponent(returnTo))
    } else {
      navigate('/proposals')
    }
  }
  const backLabel = returnTo ? 'Back' : 'Back to Proposals'

  return (
    <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8">
      <MobilePageHeader
        sticky
        title={proposal.title}
        subtitle={`Proposal #${proposal.proposal_number}`}
        onBack={handleBack}
        backLabel={backLabel}
        actions={
          <>
            <button
              type="button"
              onClick={() => navigate(`/proposals/edit/${id}`)}
              className={`${mobile.primaryBtn} !min-h-[40px] !px-3 text-xs sm:text-sm hidden sm:inline-flex`}
            >
              <EditIcon className="w-4 h-4" />
              Edit
            </button>
            <button
              type="button"
              onClick={() => setActionsOpen(true)}
              className={`${mobile.secondaryBtn} !min-h-[40px] !px-3 text-xs sm:text-sm`}
              aria-label="More actions"
            >
              <MoreHorizontalIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Actions</span>
            </button>
          </>
        }
      />

      <div className={`${mobile.surface} flex flex-col min-h-0`}>
        <ViewTabBar
          tabs={[
            { id: 'overview', label: 'Overview' },
            { id: 'billing', label: 'Billing', full: 'Billing & Financials' },
            { id: 'items', label: 'Line Items' },
            { id: 'notes', label: 'Notes', full: 'Notes & Activity' },
            { id: 'request', label: 'Request' },
          ]}
          activeTab={activeTab}
          onChange={setActiveTab}
        />

          <div className="p-3 sm:p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <ViewStatCard
                    icon={<FileTextIcon className="w-5 h-5 text-orange-600" />}
                    title="Line Items"
                    value={lineItems.length}
                  />
                  <ViewStatCard
                    icon={<DollarSignIcon className="w-5 h-5 text-blue-600" />}
                    title="Total"
                    value={`$${Number(proposal.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  />
                  <ViewStatCard
                    icon={<BriefcaseIcon className="w-5 h-5 text-purple-600" />}
                    title="Jobs Created"
                    value={proposal.jobs_created || 0}
                  />
                </div>

                {id && (
                  <ConversationsPanel
                    entityType="proposal"
                    entityId={id}
                    customerId={customer?.id}
                  />
                )}

                <ViewSection
                  title="Proposal Information"
                  icon={<FileTextIcon className="w-6 h-6" />}
                  tone="blue"
                >
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Proposal Title
                      </label>
                      <p className="text-sm text-gray-900">{proposal.title || 'N/A'}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                        <div className="mt-1">
                          {isEditingStatus ? (
                            <div className="flex items-center gap-2">
                              <select
                                value={statusValue}
                                onChange={(e) => setStatusValue(e.target.value)}
                                className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                disabled={savingStatus}
                              >
                                <option value="draft">Draft</option>
                                <option value="sent">Sent</option>
                                <option value="viewed">Viewed</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                                <option value="expired">Expired</option>
                              </select>
                              <button
                                onClick={handleSaveStatus}
                                disabled={savingStatus}
                                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                              >
                                {savingStatus ? (
                                  <>
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                    Saving...
                                  </>
                                ) : (
                                  <>
                                    <SaveIcon className="w-3 h-3" />
                                    Save
                                  </>
                                )}
                              </button>
                              <button
                                onClick={handleCancelEditStatus}
                                disabled={savingStatus}
                                className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                                  proposal.status
                                )}`}
                              >
                                {proposal.status.charAt(0).toUpperCase() + proposal.status.slice(1)}
                              </span>
                              <button
                                onClick={() => setIsEditingStatus(true)}
                                className="text-blue-600 hover:text-blue-800 text-xs underline"
                              >
                                Edit
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Service Type
                        </label>
                        <p className="text-sm text-gray-900 capitalize">
                          {getServiceTypeLabel(proposal.service_type) || 'Not specified'}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Job Type
                        </label>
                        <p className="text-sm text-gray-900">
                          {proposal.contract_type === 'recurring'
                            ? 'Recurring Service'
                            : proposal.contract_type === 'as_needed'
                              ? 'Ongoing (As Needed)'
                              : proposal.contract_type === 'one_time'
                                ? 'One-Time Project'
                                : 'Not specified'}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Valid Until
                        </label>
                        <p className="text-sm text-gray-900">
                          {proposal.valid_until
                            ? new Date(proposal.valid_until).toLocaleDateString()
                            : '—'}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Template Type
                        </label>
                        <p className="text-sm text-gray-900">
                          {proposal.template_type
                            ? proposal.template_type
                                .split('_')
                                .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ')
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* DSNY Integration Indicator - Outside the grid for cleaner layout */}
                  {proposal.dsny_integration && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm font-medium text-blue-800">DSNY Integration Enabled</span>
                      </div>
                      <p className="text-xs text-blue-700 mt-1 ml-7">
                        {proposal.dsny_intent_message || 'Services will be scheduled based on DSNY pickup days for this property.'}
                      </p>
                    </div>
                  )}
                </ViewSection>

                {/* Conversion Tracking Section */}
                {(proposal.converted_to_contract_id || proposal.jobs_created > 0 || proposal.fully_converted) && (
                  <ViewSection title="Conversion Status" tone="green">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {proposal.converted_to_contract_id && (
                        <div className="bg-white p-3 rounded-lg border border-green-200">
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Converted to Contract
                          </label>
                          <button
                            onClick={() => navigate(`/contracts/details/${proposal.converted_to_contract_id}`)}
                            className="text-sm text-blue-600 hover:underline text-left"
                            title={convertedContractInfo?.title || ''}
                          >
                            {convertedContractInfo?.contract_number
                              ? `Contract ${convertedContractInfo.contract_number} →`
                              : 'View Contract →'}
                          </button>
                          {convertedContractInfo?.title && (
                            <p className="text-xs text-gray-500 mt-1 truncate">
                              {convertedContractInfo.title}
                            </p>
                          )}
                        </div>
                      )}
                      {proposal.jobs_created > 0 && (
                        <div className="bg-white p-3 rounded-lg border border-green-200">
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Jobs Created
                          </label>
                          <p className="text-sm font-medium text-gray-900">{proposal.jobs_created} job(s)</p>
                        </div>
                      )}
                      <div className="bg-white p-3 rounded-lg border border-green-200">
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          Conversion Status
                        </label>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          proposal.fully_converted
                            ? 'bg-green-100 text-green-800'
                            : proposal.partial_conversion
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                        }`}>
                          {proposal.fully_converted
                            ? 'Fully Converted'
                            : proposal.partial_conversion
                              ? 'Partially Converted'
                              : 'Not Converted'}
                        </span>
                      </div>
                    </div>
                    {proposal.conversion_date && (
                      <p className="text-xs text-gray-500 mt-3">
                        Converted on: {new Date(proposal.conversion_date).toLocaleDateString()}
                      </p>
                    )}
                  </ViewSection>
                )}

                {(proposal.start_date || proposal.end_date) && (
                  <ViewSection
                    title="Proposed Dates"
                    icon={<CalendarIcon className="w-6 h-6" />}
                    tone="sky"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Start Date</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {proposal.start_date
                            ? new Date(proposal.start_date).toLocaleDateString()
                            : 'To be determined'}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">End Date</label>
                        <p className="mt-1 text-sm text-gray-900">
                          {proposal.end_date
                            ? new Date(proposal.end_date).toLocaleDateString()
                            : 'To be determined'}
                        </p>
                      </div>
                    </div>
                  </ViewSection>
                )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-6">
                    <ViewSection
                      title={customer ? 'Customer & Property' : lead ? 'Lead & Project' : 'Customer/Lead & Property/Project'}
                      icon={<UserIcon className="w-6 h-6" />}
                      tone="teal"
                    >

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {customer ? 'Customer' : lead ? 'Lead' : 'Customer/Lead'}
                          </label>
                          {customer && customer.id ? (
                            <button
                              onClick={() => {
                                const returnTo = encodeURIComponent(`/proposals/view/${id}`)
                                navigate(`/customers/view/${customer.id}?returnTo=${returnTo}`)
                              }}
                              className="text-sm text-blue-600 hover:text-blue-800 hover:underline text-left"
                            >
                              {customer.company_name ||
                                `${customer.contact_first_name || ''} ${
                                  customer.contact_last_name || ''
                                }`.trim() ||
                                'N/A'}
                            </button>
                          ) : lead && lead.id ? (
                            <button
                              onClick={() => {
                                const returnTo = encodeURIComponent(`/proposals/view/${id}`)
                                navigate(`/leads/view/${lead.id}?returnTo=${returnTo}`)
                              }}
                              className="text-sm text-blue-600 hover:text-blue-800 hover:underline text-left"
                            >
                              {lead.company_name ||
                                `${lead.contact_first_name || ''} ${
                                  lead.contact_last_name || ''
                                }`.trim() ||
                                'N/A'}
                            </button>
                          ) : (
                            <p className="text-sm text-gray-900">N/A</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {customer ? 'Property' : lead ? 'Project' : 'Property/Project'}
                          </label>
                          {property && property.id && customer ? (
                            <button
                              onClick={() => {
                                const returnTo = encodeURIComponent(`/proposals/view/${id}`)
                                navigate(`/properties/view/${property.id}?returnTo=${returnTo}`)
                              }}
                              className="text-sm text-blue-600 hover:text-blue-800 hover:underline text-left"
                            >
                              {property.name || property.address || 'N/A'}
                            </button>
                          ) : (
                            <p className="text-sm text-gray-900">
                              {property
                                ? property.name || property.address || 'N/A'
                                : project
                                ? project.address || 'N/A'
                                : 'N/A'}
                            </p>
                          )}
                        </div>
                      </div>
                    </ViewSection>

                    <ViewSection
                      title="Additional Information"
                      icon={<BuildingIcon className="w-6 h-6" />}
                      tone="sky"
                    >

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Company Name
                          </label>
                          <p className="text-sm text-gray-900">
                            {customer?.company_name || lead?.company_name || 'N/A'}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Property Type
                          </label>
                          <p className="text-sm text-gray-900">
                            {property?.property_type
                              ? property.property_type
                                  .split('_')
                                  .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                                  .join(' ')
                              : project?.type
                              ? project.type
                                  .split(' ')
                                  .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                                  .join(' ')
                              : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </ViewSection>
                  </div>

                  <div className="space-y-6">
                    <ViewSection
                      title="Contact Information"
                      icon={<MailIcon className="w-6 h-6" />}
                      tone="green"
                    >

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Contact Name
                          </label>
                          <p className="text-sm text-gray-900">
                            {(() => {
                              if (customer) {
                                return `${customer.contact_first_name || ''} ${
                                  customer.contact_last_name || ''
                                }`.trim() || 'N/A'
                              }
                              if (lead) {
                                const primaryContact = lead.contacts && lead.contacts[0] ? lead.contacts[0] : null
                                if (primaryContact) {
                                  if (primaryContact.name) {
                                    return primaryContact.name
                                  }
                                  if (primaryContact.first_name || primaryContact.last_name) {
                                    return `${primaryContact.first_name || ''} ${
                                      primaryContact.last_name || ''
                                    }`.trim()
                                  }
                                }
                                if (lead.contact_first_name || lead.contact_last_name) {
                                  return `${lead.contact_first_name || ''} ${
                                    lead.contact_last_name || ''
                                  }`.trim()
                                }
                              }
                              return 'N/A'
                            })()}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Contact Email
                          </label>
                          <p className="text-sm text-gray-900">
                            {(() => {
                              if (customer) {
                                return customer.email || 'N/A'
                              }
                              if (lead) {
                                const primaryContact = lead.contacts && lead.contacts[0] ? lead.contacts[0] : null
                                return primaryContact?.email || lead.email || 'N/A'
                              }
                              return 'N/A'
                            })()}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Contact Phone
                          </label>
                          <p className="text-sm text-gray-900">
                            {(() => {
                              if (customer) {
                                return formatPhoneLink(customer.phone)
                              }
                              if (lead) {
                                const primaryContact = lead.contacts && lead.contacts[0] ? lead.contacts[0] : null
                                return formatPhoneLink(primaryContact?.phone || lead.phone)
                              }
                              return 'N/A'
                            })()}
                          </p>
                        </div>
                        {(customer?.website || lead?.company_website) && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Website
                            </label>
                            <p className="text-sm text-gray-900">
                              {formatWebsiteLink(customer?.website || lead?.company_website)}
                            </p>
                          </div>
                        )}
                      </div>
                    </ViewSection>
                  </div>
                </div>

                <ViewSection
                  title="Proposal Description"
                  icon={<FileTextIcon className="w-6 h-6" />}
                  tone="indigo"
                >
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">
                    {proposal.description || 'No description provided'}
                  </p>
                </ViewSection>
              </div>
            )}

            {activeTab === 'billing' && (
              <div className="space-y-6">
                <ViewSection
                  title="Billing Setup"
                  icon={<DollarSignIcon className="w-6 h-6" />}
                  tone="blue"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Billing Frequency</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {proposal.billing_frequency
                          ? proposal.billing_frequency
                              .split('_')
                              .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
                              .join(' ')
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Billing Method</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {proposal.billing_method
                          ? proposal.billing_method
                              .split('_')
                              .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
                              .join(' ')
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Payment Method</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {proposal.payment_method
                          ? proposal.payment_method
                              .split('_')
                              .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
                              .join(' ')
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Payment Terms</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {proposal.payment_terms ? `Net ${proposal.payment_terms}` : '—'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Late Fee</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {proposal.late_fee_percentage ? `${proposal.late_fee_percentage}%` : '—'}
                      </p>
                    </div>
                    {proposal.monthly_amount ? (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Monthly Amount</label>
                        <p className="mt-1 text-sm text-gray-900">
                          ${Number(proposal.monthly_amount || 0).toFixed(2)}
                        </p>
                      </div>
                    ) : null}
                  </div>

                  {proposal.billing_frequency === 'progress' && billingMilestones.length > 0 && (
                    <MilestoneScheduleEditor
                      className="mt-4 border-t border-gray-200 pt-4"
                      milestones={billingMilestones}
                      totalAmount={proposal.total || 0}
                      onChange={() => {}}
                      readOnly
                    />
                  )}
                </ViewSection>

                <ViewSection
                  title="Pricing Details"
                  icon={<DollarSignIcon className="w-6 h-6" />}
                  tone="emerald"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {(() => {
                          if (!proposal || !lineItems || lineItems.length === 0) return 'Subtotal'

                          // Check if there are variable quantity recurring items
                          const hasVariableRecurring = lineItems.some(
                            (item) => item.is_variable && item.recurrence_type && item.recurrence_type !== 'one_time'
                          )

                          if (hasVariableRecurring && proposal.billing_frequency) {
                            // Show billing period based on billing_frequency
                            const frequencyLabels: Record<string, string> = {
                              monthly: 'Monthly Subtotal',
                              quarterly: 'Quarterly Subtotal',
                              annually: 'Annual Subtotal',
                              one_time: 'Subtotal',
                            }
                            return frequencyLabels[proposal.billing_frequency] || 'Subtotal'
                          }
                          return 'Subtotal'
                        })()}
                      </label>
                      <p className="mt-1 text-lg font-semibold text-gray-900">
                        ${(() => {
                          // Recalculate subtotal from line items using same logic as create/edit
                          // For variable recurring items, use unit_price; otherwise use total_price
                          const calculatedSubtotal = lineItems.reduce((sum, item) => {
                            if (item.is_variable && item.recurrence_type && item.recurrence_type !== 'one_time') {
                              return sum + (item.unit_price || 0)
                            }
                            return sum + (item.total_price || 0)
                          }, 0)
                          // Use calculated value if line items exist, otherwise fall back to stored value
                          return (lineItems.length > 0 ? calculatedSubtotal : (proposal.subtotal || 0)).toFixed(2)
                        })()}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Sales Tax Status</label>
                      <p className="mt-1 text-lg font-semibold text-gray-900">
                        {proposal.sales_tax_status
                          ? proposal.sales_tax_status
                              .split('-')
                              .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ')
                          : 'Use Owner Default'}
                      </p>
                    </div>
                    {proposal.sales_tax_status === 'taxable' && proposal.tax_rate && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Tax Rate</label>
                        <p className="mt-1 text-lg font-semibold text-gray-900">
                          {proposal.tax_rate || 0}%
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="text-sm font-medium text-gray-500">Tax Amount</label>
                      <p className="mt-1 text-lg font-semibold text-gray-900">
                        ${(proposal.tax_amount || 0).toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {(() => {
                          if (!proposal || !lineItems || lineItems.length === 0) return 'Total'

                          // Check if there are variable quantity recurring items
                          const hasVariableRecurring = lineItems.some(
                            (item) => item.is_variable && item.recurrence_type && item.recurrence_type !== 'one_time'
                          )

                          if (hasVariableRecurring && proposal.billing_frequency) {
                            // Show billing period based on billing_frequency
                            const frequencyLabels: Record<string, string> = {
                              monthly: 'Monthly Total',
                              quarterly: 'Quarterly Total',
                              annually: 'Annual Total',
                              one_time: 'Total',
                            }
                            return frequencyLabels[proposal.billing_frequency] || 'Total'
                          }
                          return 'Total'
                        })()}
                      </label>
                      <p className="mt-1 text-lg font-semibold text-blue-600">
                        ${(() => {
                          // Recalculate total from line items
                          const calculatedSubtotal = lineItems.reduce((sum, item) => {
                            if (item.is_variable && item.recurrence_type && item.recurrence_type !== 'one_time') {
                              return sum + (item.unit_price || 0)
                            }
                            return sum + (item.total_price || 0)
                          }, 0)
                          // Calculate tax
                          const shouldCalculateTax = proposal.sales_tax_status === 'taxable'
                          const taxAmount = shouldCalculateTax ? calculatedSubtotal * ((proposal.tax_rate || 0) / 100) : 0
                          const calculatedTotal = calculatedSubtotal + taxAmount
                          // Use calculated value if line items exist, otherwise fall back to stored value
                          return (lineItems.length > 0 ? calculatedTotal : (proposal.total || proposal.total_amount || 0)).toFixed(2)
                        })()}
                      </p>
                    </div>
                  </div>
                </ViewSection>

                {proposal.customer_notes && (
                  <ViewSection title="Customer Notes" icon={<FileTextIcon className="w-6 h-6" />} tone="orange">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">
                      {proposal.customer_notes}
                    </p>
                  </ViewSection>
                )}

                {proposal.terms_and_conditions && (
                  <ViewSection title="Terms & Conditions" icon={<FileTextIcon className="w-6 h-6" />} tone="indigo">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">
                      {proposal.terms_and_conditions}
                    </p>
                  </ViewSection>
                )}
              </div>
            )}

            {/* Line Items Tab */}
            {activeTab === 'items' && (
              <div className="space-y-6">
                {lineItems.length > 0 ? (
                  <ViewSection title="Line Items" icon={<FileTextIcon className="w-6 h-6" />} tone="orange">
                    {(() => {
                      const formatUnitType = (unitType: unknown) => {
                        const raw = String(unitType || '').toLowerCase().trim()
                        if (!raw) return 'N/A'
                        const map: Record<string, string> = {
                          monthly: 'Per Month',
                          hourly: 'Per Hour',
                          sqft: 'Per Sq Ft',
                          visit: 'Per Visit',
                          unit: 'Per Unit',
                          flat: 'Flat Rate',
                        }
                        if (map[raw]) return map[raw]
                        return raw
                          .split('_')
                          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                          .join(' ')
                      }
                      const formatRecurrence = (item: any) => {
                        if (!item.recurrence_type || item.recurrence_type === 'one_time') {
                          return 'One-time'
                        }
                        const interval = Number(item.recurrence_interval || 0)
                        const unit = item.interval_unit
                        if (unit === 'days_per_week' && interval > 0) {
                          return `${interval} day${interval === 1 ? '' : 's'}/week`
                        }
                        if (unit === 'days_per_month' && interval > 0) {
                          return `${interval} day${interval === 1 ? '' : 's'}/month`
                        }
                        if (unit === 'every_x_weeks' && interval > 0) {
                          return `every ${interval} week${interval === 1 ? '' : 's'}`
                        }
                        if (unit === 'every_x_months' && interval > 0) {
                          return `every ${interval} month${interval === 1 ? '' : 's'}`
                        }
                        return String(item.recurrence_type).replace(/_/g, ' ')
                      }
                      const formatLineTotal = (item: any) => {
                        if (
                          item.is_variable &&
                          item.recurrence_type &&
                          item.recurrence_type !== 'one_time' &&
                          proposal?.billing_frequency
                        ) {
                          const frequencyLabels: Record<string, string> = {
                            monthly: '/month',
                            quarterly: '/quarter',
                            annually: '/year',
                            one_time: '',
                          }
                          const interval = frequencyLabels[proposal.billing_frequency] || ''
                          return `$${item.unit_price?.toFixed(2) || '0.00'}${interval}`
                        }
                        if (item.is_variable) return 'Variable'
                        return `$${item.total_price?.toFixed(2) || '0.00'}`
                      }
                      const subtotalLabel = (() => {
                        if (!proposal || !lineItems || lineItems.length === 0) return 'Subtotal:'
                        const hasVariableRecurring = lineItems.some(
                          (item) =>
                            item.is_variable &&
                            item.recurrence_type &&
                            item.recurrence_type !== 'one_time'
                        )
                        if (hasVariableRecurring && proposal.billing_frequency) {
                          const frequencyLabels: Record<string, string> = {
                            monthly: 'Monthly Subtotal:',
                            quarterly: 'Quarterly Subtotal:',
                            annually: 'Annual Subtotal:',
                            one_time: 'Subtotal:',
                          }
                          return frequencyLabels[proposal.billing_frequency] || 'Subtotal:'
                        }
                        return 'Subtotal:'
                      })()
                      const subtotalValue = lineItems
                        .reduce((sum, item) => {
                          if (
                            item.is_variable &&
                            item.recurrence_type &&
                            item.recurrence_type !== 'one_time'
                          ) {
                            return sum + (item.unit_price || 0)
                          }
                          return sum + (item.total_price || 0)
                        }, 0)
                        .toFixed(2)

                      return (
                        <>
                          <ul className="space-y-2 lg:hidden">
                            {lineItems.map((item) => (
                              <li
                                key={item.id}
                                className="rounded-xl border border-gray-200 p-3 space-y-1.5"
                              >
                                <p className="text-sm font-medium text-gray-900 break-words">
                                  {item.description}
                                </p>
                                {item.service_item && (
                                  <p className="text-xs text-blue-600 font-medium">
                                    From Catalog: {item.service_item.name}
                                  </p>
                                )}
                                <div className="flex items-center gap-2 flex-wrap">
                                  {item.is_variable && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-800 uppercase">
                                      Variable
                                    </span>
                                  )}
                                  {item.service_type && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700">
                                      {String(item.service_type).replace(/_/g, ' ')}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-600">
                                  Qty {item.quantity} · {formatUnitType(item.unit_type)} ·{' '}
                                  {formatRecurrence(item)}
                                </p>
                                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600 pt-0.5">
                                  <span>Rate ${item.unit_price?.toFixed(2) || '0.00'}</span>
                                  <span className="font-semibold text-gray-900">
                                    {formatLineTotal(item)}
                                  </span>
                                </div>
                              </li>
                            ))}
                            <li className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 flex items-center justify-between text-sm">
                              <span className="font-medium text-gray-700">{subtotalLabel}</span>
                              <span className="font-bold text-gray-900">${subtotalValue}</span>
                            </li>
                          </ul>
                          <div className="hidden lg:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Description
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Quantity
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Unit Type
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Recurrence
                                  </th>
                                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Unit Price
                                  </th>
                                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Total
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {lineItems.map((item) => (
                                  <tr key={item.id}>
                                    <td className="px-4 py-3 text-sm text-gray-900">
                                      <div className="flex flex-col gap-1">
                                        <span>{item.description}</span>
                                        {item.service_item && (
                                          <span className="text-xs text-blue-600 font-medium">
                                            From Catalog: {item.service_item.name}
                                          </span>
                                        )}
                                        <div className="flex items-center gap-2 flex-wrap">
                                          {item.is_variable && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-800 uppercase">
                                              Variable
                                            </span>
                                          )}
                                          {item.service_type && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700">
                                              {String(item.service_type).replace(/_/g, ' ')}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{item.quantity}</td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                      {formatUnitType(item.unit_type)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                      {formatRecurrence(item) === 'One-time' ? (
                                        <span className="text-gray-400">One-time</span>
                                      ) : (
                                        formatRecurrence(item)
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                                      ${item.unit_price?.toFixed(2) || '0.00'}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                                      {formatLineTotal(item)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot className="bg-gray-50">
                                <tr>
                                  <td
                                    colSpan={5}
                                    className="px-4 py-3 text-sm font-medium text-gray-900 text-right"
                                  >
                                    {subtotalLabel}
                                  </td>
                                  <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                    ${subtotalValue}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </>
                      )
                    })()}
                  </ViewSection>
                ) : (
                  <ViewSection title="Line Items" icon={<FileTextIcon className="w-6 h-6" />} tone="orange">
                    <ViewEmptyState
                      tone="orange"
                      icon={<FileTextIcon className="w-10 h-10 text-orange-300" />}
                      message="No line items have been added to this proposal."
                    />
                  </ViewSection>
                )}
              </div>
            )}

            {activeTab === 'notes' && (
              <NotesAndActivityPanel
                notes={notes.map((n) => ({ note: n.note, timestamp: n.timestamp }))}
                activities={activities}
                notesTitle="Proposal Notes"
                newNote={newNote}
                onNewNoteChange={setNewNote}
                onAddNote={handleAddNote}
                savingNote={savingNote}
              />
            )}

            {/* Request Tab */}
            {activeTab === 'request' && (
              <div className="space-y-6">
                {loadingRequest ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600">Loading request information...</p>
                  </div>
                ) : proposalRequest ? (
                  <ProposalRequestTabPanel
                    request={proposalRequest}
                    contact={proposalRequestContact}
                    serviceCategories={serviceCategories}
                    viewRequestHref={
                      id
                        ? `/proposal-requests/view/${id}?returnTo=${encodeURIComponent(`/proposals/view/${id}`)}`
                        : undefined
                    }
                  />
                ) : (
                  <ViewEmptyState
                    tone="indigo"
                    icon={<FileTextIcon className="w-10 h-10 text-indigo-300" />}
                    message="No proposal request found for this proposal."
                  />
                )}
              </div>
            )}
          </div>
      </div>

      <div className="native-action-bar flex items-center justify-between gap-3 max-w-6xl mx-auto lg:mt-4 lg:rounded-xl lg:border lg:border-gray-200">
        <button type="button" onClick={handleBack} className={mobile.secondaryBtn}>
          {backLabel}
        </button>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setActionsOpen(true)} className={mobile.secondaryBtn}>
            <MoreHorizontalIcon className="w-4 h-4" />
            Actions
          </button>
          <button
            type="button"
            onClick={() => navigate(`/proposals/edit/${id}`)}
            className={mobile.primaryBtn}
          >
            <EditIcon className="w-4 h-4" />
            Edit
          </button>
        </div>
      </div>

      <MobileSheet open={actionsOpen} onClose={() => setActionsOpen(false)} title="Proposal actions">
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              handleCreateContract()
            }}
            className={`${mobile.successBtn} w-full`}
          >
            <FileTextIcon className="w-4 h-4" />
            Convert to Contract
          </button>
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              handleCreateJob()
            }}
            className={`${mobile.primaryBtn} w-full !bg-purple-600 hover:!bg-purple-700`}
          >
            <PlusIcon className="w-4 h-4" />
            Convert to Job
          </button>
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              handleGeneratePdf()
            }}
            disabled={generatingPdf}
            className={`${mobile.secondaryBtn} w-full`}
          >
            <FileTextIcon className="w-4 h-4" />
            {generatingPdf ? 'Generating…' : 'Generate PDF'}
          </button>
          <button
            type="button"
            onClick={() => {
              setActionsOpen(false)
              navigate(`/proposals/edit/${id}`)
            }}
            className={`${mobile.primaryBtn} w-full`}
          >
            <EditIcon className="w-4 h-4" />
            Edit Proposal
          </button>
        </div>
      </MobileSheet>

      <ConvertLeadModal
        lead={lead}
        open={showConvertModal && !!lead}
        variant="proposal"
        confirmLabel="Confirm & Approve Proposal"
        serviceCategories={serviceCategories}
        onClose={() => {
          setShowConvertModal(false)
          setPendingStatus(null)
        }}
        onSuccess={handleProposalConversionSuccess}
        onError={(message) => alert(message)}
      />

      {/* Create Job Modal - shown after successful conversion */}
      {showCreateJobModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl p-6 max-w-md w-full mx-0 sm:mx-4">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Conversion Successful!</h3>
              <p className="text-sm text-gray-600 mb-6">
                Lead has been converted to a customer and the proposal has been approved.
              </p>
              <p className="text-sm text-gray-700 font-medium mb-6">
                What would you like to do next?
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    setShowCreateJobModal(false)
                    navigate(`/jobs/create?proposalId=${id}`)
                  }}
                  className={mobile.primaryBtn}
                >
                  Create Job
                </button>
                <button
                  onClick={() => {
                    setShowCreateJobModal(false)
                    navigate(`/contracts/create?proposalId=${id}`)
                  }}
                  className={mobile.successBtn}
                >
                  Create Contract
                </button>
                <button
                  onClick={() => setShowCreateJobModal(false)}
                  className={mobile.secondaryBtn}
                >
                  Not Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MobilePage>
  )
}
